function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import PropTypes from 'prop-types';
import React from 'react';
import { Image as RNImage } from 'react-native';
import { stylePropTypes } from '@dkrm/general-libs/theme/styles';
import { getResponsiveImageUrl } from '@dkrm/general-libs/Utils/imageUtils';
import { getUrlWithOverridenQueries } from '@dkrm/general-libs/Utils/urlUtils';
import { makeCancellable } from '@dkrm/general-libs/Utils/promise';
import Image from '../Image';

/**
 * Image component with the ability to be displayed uncropped.
 * Design Decision:
 * - It returns nothing if source.height is not set.
 * - It uses height to adjust its width automatically.
 *
 */
class AspectRatioImage extends React.Component {
  constructor(...args) {
    super(...args);
    this.state = {
      source: undefined
    };
  }
  componentDidMount() {
    const {
      source,
      additionalQuery
    } = this.props;
    this.setSource(source, additionalQuery);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      source
    } = this.props;
    if (nextProps.source !== source) {
      this.setSource(nextProps.source, nextProps.additionalQuery);
    }
  }
  componentWillUnmount() {
    this.cancelGetImageSize();
  }
  async setSource(source, additionalQuery) {
    if (source && (!source.height || Number.isNaN(source.height)) && (!source.width || Number.isNaN(source.width))) {
      return;
    }
    if (!source.uri) return;
    const responsiveImageUrl = getResponsiveImageUrl(source.uri, source.height ? undefined : source.width, source.height || undefined);
    const imageUrl = getUrlWithOverridenQueries(responsiveImageUrl, additionalQuery);
    const getImageSize = this.getImageSize(imageUrl);
    const {
      onRatioLoaded
    } = this.props;
    try {
      const {
        sourceWidth,
        sourceHeight
      } = await getImageSize;
      this.setState({
        source: {
          ...source,
          width: sourceWidth,
          height: sourceHeight,
          uri: imageUrl
        },
        widthProportion: sourceWidth,
        heightProportion: sourceHeight
      });
      if (onRatioLoaded) onRatioLoaded();
    } catch (err) {
      if (!err.isCancelled) console.error(err);
    }
  }
  getImageSize(imageUrl) {
    const promise = new Promise(resolve => RNImage.getSize(imageUrl, (sourceWidth, sourceHeight) => resolve({
      sourceWidth,
      sourceHeight
    })));
    this.cancelGetImageSize();
    this._getImageSize = makeCancellable(promise);
    return this._getImageSize;
  }
  cancelGetImageSize() {
    if (this._getImageSize) this._getImageSize.cancel();
  }
  render() {
    const {
      style,
      forwardedRef,
      ...rest
    } = this.props;
    const {
      source,
      widthProportion,
      heightProportion
    } = this.state;
    const width = rest.source.height && widthProportion * rest.source.height / heightProportion || rest.source.width;
    const height = rest.source.height && rest.source.height || heightProportion * rest.source.width / widthProportion;
    return /*#__PURE__*/React.createElement(Image, _extends({}, rest, {
      source: source,
      style: [{
        width,
        height
      }, style],
      ref: forwardedRef
    }));
  }
}
AspectRatioImage.defaultProps = {
  source: undefined,
  additionalQuery: {},
  style: undefined,
  onRatioLoaded: () => undefined,
  forwardedRef: undefined
};
AspectRatioImage.propTypes = process.env.NODE_ENV !== "production" ? {
  source: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired
  }),
  additionalQuery: PropTypes.shape(),
  style: stylePropTypes,
  onRatioLoaded: PropTypes.func,
  forwardedRef: PropTypes.shape()
} : {};
export default /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(AspectRatioImage, _extends({}, props, {
  forwardedRef: ref
})));